import {
  Button,
  CollapsibleSection,
  ScoreSequence,
  Section,
  cn,
  linkClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { groupPracticesBySubject } from '../../lib/group-practices-by-subject.js'
import { useFormat } from '../../lib/format.js'

/** One practice's run, as the progress query selects it: newest attempt first. */
export interface PracticeRun {
  practiceId: string
  title: string
  subject: { id: string; code: string; name: string }
  attemptCount: number
  /** Null when the tutor's plan does not include complete progress tracking. */
  bestPercent: number | null
  attempts: readonly { attemptId: string; percent: number; submittedAt: string }[]
}

/** What the summary may offer, decided by the server and handed down. */
export type PracticeAction =
  | { kind: 'again'; starting: boolean; onStart: () => void }
  | { kind: 'review'; attemptId: string }
  | { kind: 'none' }

/** Dense runs keep their dated detail list available beneath the complete chart. */
export const ATTEMPT_DETAIL_THRESHOLD = 6

export interface PracticeProgressListProps {
  practices: readonly PracticeRun[]
  actionFor: (practice: PracticeRun) => PracticeAction
}

/**
 * A student's progress, practice by practice, filed by subject.
 *
 * The subject is a heading, not a score: a practice is the thing a student
 * can compare with itself, so each subject simply holds its practices, most
 * recently done first, and nothing is averaged across them. A subject with
 * several practices is not given one number, because that number would be
 * the mean of unrelated work.
 */
export function PracticeProgressList({ practices, actionFor }: PracticeProgressListProps) {
  const format = useFormat()
  const groups = groupPracticesBySubject(practices)

  return (
    <div className="flex flex-col gap-16">
      {groups.map((group) => (
        <Section
          key={group.subject.id}
          id={`progress-${group.subject.id}`}
          title={group.subject.name}
          variant="ruled"
          aside={
            <span className="text-body-sm font-medium tabular-nums text-text-secondary">
              {format.count('practices', group.practices.length)}
            </span>
          }
        >
          <ul className="divide-y divide-border">
            {group.practices.map((practice) => (
              <li key={practice.practiceId} className="py-6 first:pt-2 last:pb-0">
                <PracticeProgressSummary practice={practice} action={actionFor(practice)} />
              </li>
            ))}
          </ul>
        </Section>
      ))}
    </div>
  )
}

/**
 * One practice: what it is, how it went last time, what happened each time,
 * and what can be done about it — in that order, on one flat row.
 *
 * ── Latest and best ──────────────────────────────────────────────────────
 *
 * Latest is where the student is now, so it is the larger number; best is
 * what they have reached, beside it and smaller. With one attempt the two are
 * the same number, so there is one — "Latest". The evolution still shows its
 * one labelled point, ready to become a line after the next attempt. A tie for
 * best is one "Best", never a mark on each tied attempt.
 *
 * ── Nothing is judged ────────────────────────────────────────────────────
 *
 * A score is written in the ordinary text colour whatever it is. There is no
 * "improving" or "declining": the sequence shows what happened, and the
 * student reads it. Every figure is the server's — the percentages, the best
 * and the count — and the only thing worked out here is which attempt came
 * first, from the dates the server sent.
 */
export function PracticeProgressSummary({
  practice,
  action,
}: {
  practice: PracticeRun
  action: PracticeAction
}) {
  const t = useTranslate()
  const format = useFormat()
  const language = useLanguage()

  // The server sends the run newest first; the sequence reads oldest first,
  // and an attempt's number is its place in that order.
  const oldestFirst = [...practice.attempts]
    .sort((a, b) => Date.parse(a.submittedAt) - Date.parse(b.submittedAt))
    .map((attempt, index) => ({ ...attempt, number: index + 1 }))
  const latest = oldestFirst.at(-1)
  const many = oldestFirst.length > 1

  return (
    <div className="flex flex-col gap-4">
      <div className="grid min-w-0 gap-x-6 gap-y-5 md:grid-cols-[minmax(0,1fr)_auto] desktop:grid-cols-[minmax(13rem,0.9fr)_auto_minmax(18rem,1.6fr)_auto] desktop:items-center desktop:gap-x-8">
        <div className="min-w-0 md:col-start-1 md:row-start-1 desktop:col-start-1">
          <h3 className="text-h3 font-semibold text-text text-balance">{practice.title}</h3>
          {latest ? (
            <p className="mt-1 text-body-sm text-text-secondary">
              {`${format.count('attempts', practice.attemptCount)} · ${t(
                'progress.practice.lastOn',
                [format.date(latest.submittedAt)],
              )}`}
            </p>
          ) : null}
        </div>

        {/* Compact supporting figures. The line receives the flexible column. */}
        <dl className="grid grid-cols-2 items-start gap-x-8 gap-y-2 md:col-start-1 md:row-start-2 desktop:col-start-2 desktop:row-start-1">
          <div className="flex flex-col">
            <dt className="text-body-sm text-text-secondary">{t('progress.practice.latest')}</dt>
            <dd className="text-h2 font-bold tabular-nums text-text">
              {format.percent(latest?.percent ?? 0)}
            </dd>
          </div>
          {many && practice.bestPercent !== null ? (
            <div className="flex flex-col">
              <dt className="text-body-sm text-text-secondary">{t('progress.practice.best')}</dt>
              <dd className="text-h3 font-semibold tabular-nums text-text">
                {format.percent(practice.bestPercent)}
              </dd>
            </div>
          ) : null}
        </dl>

        {latest ? (
          <div className="min-w-0 md:col-span-2 md:row-start-3 desktop:col-span-1 desktop:col-start-3 desktop:row-start-1">
            <p className="mb-2 text-body-sm font-semibold text-text-secondary">
              {t('progress.practice.evolution')}
            </p>
            <ScoreSequence
              label={t('progress.practice.sequenceLabel', [practice.title])}
              latestLabel={t('progress.practice.latest')}
              items={oldestFirst.map((attempt) => ({
                id: attempt.attemptId,
                percent: attempt.percent,
                label: t('attempt.attemptNumber', [attempt.number]),
                shortLabel: String(attempt.number),
              }))}
            />
          </div>
        ) : null}

        {action.kind === 'again' ? (
          <div className="md:col-start-2 md:row-start-1 md:justify-self-end desktop:col-start-4 desktop:row-start-1">
            {/* The same action, words and size as the home's card, and only
                when the server says another go can be started now. Secondary:
                this screen is for reading how it went, not a list of things
                to do. */}
            <Button
              variant="secondary"
              size="lg"
              className="w-full sm:w-auto"
              onClick={action.onStart}
              loading={action.starting}
              loadingText={t('common.opening')}
              aria-label={t('learn.card.tryAgainNamed', [practice.title])}
            >
              {t('learn.card.tryAgain')}
            </Button>
          </div>
        ) : action.kind === 'review' ? (
          <div className="md:col-start-2 md:row-start-1 md:justify-self-end desktop:col-start-4 desktop:row-start-1">
            <Link
              to="/$language/learn/attempt/$attemptId"
              params={{ language, attemptId: action.attemptId }}
              className={cn(
                linkClasses({ size: 'sm' }),
                'inline-flex min-h-touch-target-min w-full items-center justify-center sm:w-auto',
              )}
              aria-label={t('progress.practice.seeTheAnswersNamed', [practice.title])}
            >
              {t('history.seeTheAnswers')}
            </Link>
          </div>
        ) : null}
      </div>

      {oldestFirst.length > ATTEMPT_DETAIL_THRESHOLD ? (
        // The chart already includes every point. This disclosure preserves
        // the dated detail that dense runs have always offered.
        <CollapsibleSection title={t('progress.practice.allAttempts')}>
          <ol className="flex flex-col gap-2">
            {[...oldestFirst].reverse().map((attempt) => (
              <li
                key={attempt.attemptId}
                className="flex flex-wrap items-baseline justify-between gap-x-4 text-body-sm"
              >
                <span className="text-text">
                  {`${t('attempt.attemptNumber', [attempt.number])} · ${format.date(attempt.submittedAt)}`}
                </span>
                <span className="font-semibold tabular-nums text-text">
                  {format.percent(attempt.percent)}
                </span>
              </li>
            ))}
          </ol>
        </CollapsibleSection>
      ) : null}
    </div>
  )
}
